import { useRef, useState, type ReactNode } from 'react'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { IconButton, Row, Sheet } from '../ui'

export interface RowMoreAction {
  label: string
  icon: ReactNode
  onSelect: () => void
}

/**
 * a2-32: the "…" at the end of a list item's row, or a Year day or flight —
 * the visible way to what a swipe does (Doug chose this over a one-time
 * coachmark). Swipe still works; these are the same actions, behind a control
 * someone can see.
 *
 * Built the way `ListMenu` is, a `Sheet` of `Row`s, because that is the one
 * shape a "…" opens in this app. Edit first and Remove last; `extra` sits
 * between them for whatever else the row could only do by swipe or long
 * press (Mark ordered, a flag, Indent, Move to aisle) — a menu that left
 * those out would leave them exactly as hidden as they were.
 *
 * Remove is the swipe's own `onDelete`, so it toasts with Undo the same way;
 * neither path asks for confirmation (F7).
 */
export function RowMore({ name, onEdit, onRemove, extra = [] }: {
  /** The item's own words: the sheet's title and the button's accessible name. */
  name: string
  onEdit?: () => void
  onRemove?: () => void
  extra?: RowMoreAction[]
}) {
  const [open, setOpen] = useState(false)
  // Whether the sheet is closing because an action was picked rather than
  // dismissed. Only a dismissal hands focus back to the "…" — see
  // `SheetProps.onCloseAutoFocus` for what a picked Edit would lose to it.
  const picked = useRef(false)

  const actions: RowMoreAction[] = [
    ...(onEdit ? [{ label: 'Edit', icon: <Pencil size={20} aria-hidden="true" />, onSelect: onEdit }] : []),
    ...extra,
    ...(onRemove ? [{ label: 'Remove', icon: <Trash2 size={20} aria-hidden="true" />, onSelect: onRemove }] : []),
  ]

  return (
    <>
      <IconButton
        label={`More on "${name}"`} size="sm" className="rowmore"
        onClick={() => { picked.current = false; setOpen(true) }}
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </IconButton>
      {open && (
        <Sheet
          open title={name}
          onOpenChange={o => { if (!o) setOpen(false) }}
          onCloseAutoFocus={ev => { if (picked.current) ev.preventDefault() }}
        >
          {actions.map(a => (
            <Row
              key={a.label} className="u-row--list" leading={a.icon} title={a.label} meta={null}
              onPress={() => { picked.current = true; setOpen(false); a.onSelect() }}
            />
          ))}
        </Sheet>
      )}
    </>
  )
}
